import { cn } from "@nexxt/ui";
import { useRef, useState } from "react";
import { m } from "../../paraglide/messages.js";
import { useStill } from "../motion/motion";
import { useInView } from "../motion/use-in-view";
import { useSceneTimeline } from "../motion/use-scene-timeline";
import { apps, type FlowId, flows, type NodeId, nodes, pathOf, position, route } from "../suite-map";
import { MapRings, mapViewBox } from "./map-rings";

// The map of the suite (ADR-286): the rings, who stands on them, and one journey at a time across them. The journeys
// take turns while the map is on screen, until the visitor picks one; the list beside the map tells the same in words.
const turns = [6500, 6500, 6500, 6500] as const;

const flowName: Record<FlowId, () => string> = {
  collection: m.flow_collection,
  invitation: m.flow_invitation,
  order: m.flow_order,
  purchase: m.flow_purchase,
};

const stepText: Record<FlowId, readonly (() => string)[]> = {
  collection: [m.flow_collection_1, m.flow_collection_2, m.flow_collection_3, m.flow_collection_4],
  invitation: [m.flow_invitation_1, m.flow_invitation_2, m.flow_invitation_3, m.flow_invitation_4],
  order: [m.flow_order_1, m.flow_order_2, m.flow_order_3, m.flow_order_4, m.flow_order_5, m.flow_order_6],
  purchase: [m.flow_purchase_1, m.flow_purchase_2, m.flow_purchase_3, m.flow_purchase_4],
};

const nodeName: Record<NodeId, () => string> = {
  nexxt: () => "NEXXT",
  backstage: () => "Backstage",
  guestlist: () => "Guestlist",
  atelier: () => "Atelier",
  frontrow: () => "Frontrow",
  showroom: m.node_showroom,
  til: m.node_til,
  bc: m.node_bc,
  ims: m.node_ims,
  gxo: m.node_gxo,
  joor: m.node_joor,
  zendesk: m.node_zendesk,
  claude: m.node_claude,
};

/** The colour of a step in the list: the app's, or none for a place or a system. */
const appOf = (id: NodeId) => ((apps as readonly NodeId[]).includes(id) ? id : "world");

export function SuiteMapSection() {
  const still = useStill();
  const figureRef = useRef<HTMLDivElement>(null);
  const inView = useInView(figureRef, 0.3);
  const [picked, setPicked] = useState<number | null>(null);
  const turn = useSceneTimeline(turns, inView && picked === null, still);
  const index = picked ?? (still ? 0 : turn);
  const flow = flows[index] ?? flows[0];
  if (!flow) return null;
  const stops = route(flow.steps);
  const on = new Set<NodeId>(stops);
  const path = pathOf(stops);

  return (
    <section id="map" className="map-section" aria-labelledby="map-title">
      <div ref={figureRef} className="map-figure">
        <svg viewBox={mapViewBox} role="img" aria-labelledby="map-svg-title map-svg-description">
          <title id="map-svg-title">{m.map_svg_title()}</title>
          <desc id="map-svg-description">{m.map_svg_description()}</desc>
          <MapRings withCore />
          <line className="map-mcp" x1={0} y1={0} x2={position("claude").x} y2={position("claude").y} />
          <path key={flow.id} className="map-flow" d={path} />
          {!still &&
            [0, 1, 2].map((i) => (
              <circle key={`${flow.id}-${i}`} r={9} className="map-packet">
                <animateMotion dur="4.5s" begin={`${i * 1.5}s`} repeatCount="indefinite" path={path} />
              </circle>
            ))}
          {(Object.keys(nodes) as NodeId[]).map((id) => (
            <MapNode key={id} id={id} on={on.has(id)} />
          ))}
        </svg>
      </div>
      <div className="map-copy">
        <p className="kicker">{m.map_kicker()}</p>
        <h2 id="map-title">{m.map_title()}</h2>
        <p className="lead">{m.map_body()}</p>
        <fieldset className="flow-picker">
          <legend className="sr-only">{m.map_flows_label()}</legend>
          {flows.map((f, i) => (
            <button
              key={f.id}
              type="button"
              aria-pressed={i === index}
              className="flow-chip"
              onClick={() => setPicked(i)}
            >
              {flowName[f.id]()}
            </button>
          ))}
        </fieldset>
        <ol className="flow-steps" aria-label={m.map_steps_label({ journey: flowName[flow.id]() })} key={flow.id}>
          {flow.steps.map((step, i) => {
            const text = stepText[flow.id][i]?.() ?? "";
            return (
              <li key={text} data-app={appOf(step)}>
                <span className="flow-step-node">{nodeName[step]()}</span>
                {text}
              </li>
            );
          })}
        </ol>
      </div>
    </section>
  );
}

function MapNode({ id, on }: { id: NodeId; on: boolean }) {
  const { kind } = nodes[id];
  const { x, y } = position(id);
  const name = nodeName[id]();
  if (kind === "core")
    return (
      <g className="map-node is-core">
        <circle r={62} />
        <text y={9} textAnchor="middle">
          {name}
        </text>
      </g>
    );
  // labels outside the dot, on the side away from the centre
  const below = y > 0;
  const radius = kind === "app" ? 30 : 13;
  return (
    <g className={cn("map-node", `is-${kind}`, on && "is-on")} data-app={appOf(id)} transform={`translate(${x} ${y})`}>
      {id === "guestlist" && (
        <>
          <circle r={58} className="map-glow" />
          <circle r={86} className="map-glow" />
        </>
      )}
      <circle r={radius} />
      <text y={below ? radius + 30 : -radius - 14} textAnchor="middle">
        {kind === "app" ? name.toUpperCase() : name}
      </text>
    </g>
  );
}
